export type DiffOperation = "equal" | "insert" | "delete";

export interface DiffSegment {
    operation: DiffOperation;
    text: string;
}

export interface DiffLine {
    indent: number;
    segments: DiffSegment[];
}

type IndentMode = "braces" | "xml" | "none";

const PILCROW = "¶";

const operationByTag: Record<string, DiffOperation> = {
    INS: "insert",
    DEL: "delete",
};

const getIndentMode = (fileName: string): IndentMode => {
    const extension = fileName.split(".").pop()?.toLowerCase();

    if (extension === "lua" || extension === "json") {
        return "braces";
    }
    if (extension === "xml") {
        return "xml";
    }
    return "none";
};

/**
 * Parses HTML produced by diff-match-patch `diff_prettyHtml` (`<ins>`, `<del>`, `<span>` wrappers,
 * with line breaks encoded as `&para;<br>`) into lines of segments.
 */
export const parseDiffHtml = (html: string): DiffSegment[][] => {
    const body = new DOMParser().parseFromString(html, "text/html").body;
    let currentLine: DiffSegment[] = [];
    const lines: DiffSegment[][] = [currentLine];

    const append = (operation: DiffOperation, text: string) => {
        const cleaned = text.replace(/[\r\n]/g, "");
        if (cleaned) {
            currentLine.push({ operation, text: cleaned });
        }
    };

    const breakLine = (operation: DiffOperation) => {
        const lastSegment = currentLine.at(-1);
        if (lastSegment?.text.endsWith(PILCROW)) {
            lastSegment.text = lastSegment.text.slice(0, -1);
            if (!lastSegment.text) {
                currentLine.pop();
            }
        }
        // keep a marker so that a removed/added empty line is still visible as a change
        if (operation !== "equal" && currentLine.length === 0) {
            currentLine.push({ operation, text: "" });
        }
        currentLine = [];
        lines.push(currentLine);
    };

    const walk = (node: Node, operation: DiffOperation) => {
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                append(operation, child.textContent ?? "");
            } else if (child.nodeName === "BR") {
                breakLine(operation);
            } else {
                walk(child, operationByTag[child.nodeName] ?? operation);
            }
        });
    };

    walk(body, "equal");

    if (lines.length > 1 && currentLine.length === 0) {
        lines.pop();
    }
    return lines;
};

const stripLeadingWhitespace = (segments: DiffSegment[]): DiffSegment[] => {
    const result: DiffSegment[] = [];
    let atLineStart = true;

    segments.forEach((segment) => {
        const text = atLineStart ? segment.text.replace(/^\s+/, "") : segment.text;
        if (text) {
            atLineStart = false;
        }
        if (text || segment.text === "") {
            result.push({ ...segment, text });
        }
    });
    return result;
};

const getNewText = (segments: DiffSegment[]): string =>
    segments
        .filter(({ operation }) => operation !== "delete")
        .map(({ text }) => text)
        .join("");

/** Masks string literals and comments (keeping offsets) so that braces inside them are ignored. */
const maskBraceNoise = (line: string): string => {
    let masked = "";
    let quote: string | undefined;
    let index = 0;

    while (index < line.length) {
        const char = line[index];
        if (quote) {
            // an escaped character never closes the string
            const length = char === "\\" ? Math.min(2, line.length - index) : 1;
            masked += "x".repeat(length);
            index += length;
            if (char === quote) {
                quote = undefined;
            }
        } else if (char === '"' || char === "'") {
            quote = char;
            masked += "x";
            index += 1;
        } else if (char === "-" && line[index + 1] === "-") {
            return masked + "x".repeat(line.length - index);
        } else {
            masked += char;
            index += 1;
        }
    }
    return masked;
};

const measureBraces = (line: string) => {
    const code = maskBraceNoise(line);
    const opened = (code.match(/{/g) ?? []).length;
    const closed = (code.match(/}/g) ?? []).length;
    const leadingClosers = (/^[\s,;}]*/.exec(code)?.[0].match(/}/g) ?? []).length;

    return { delta: opened - closed, leadingClosers };
};

/** Splits segments at an offset counted in the new text; deleted segments before the offset stay in the head. */
const splitSegments = (segments: DiffSegment[], offset: number): [DiffSegment[], DiffSegment[]] => {
    const head: DiffSegment[] = [];
    const tail: DiffSegment[] = [];
    let remaining = offset;

    segments.forEach((segment) => {
        if (remaining <= 0) {
            tail.push(segment);
            return;
        }
        if (segment.operation === "delete") {
            head.push(segment);
            return;
        }
        if (segment.text.length <= remaining) {
            head.push(segment);
        } else {
            head.push({ ...segment, text: segment.text.slice(0, remaining) });
            tail.push({ ...segment, text: segment.text.slice(remaining) });
        }
        remaining -= segment.text.length;
    });
    return [head, tail];
};

/** Offset of the trailing closing braces that close tables opened on previous lines, if preceded by content. */
const findTrailingClosers = (line: string): number | undefined => {
    const code = maskBraceNoise(line);
    let runStart = code.length;
    while (runStart > 0 && (code[runStart - 1] === "}" || /\s/.test(code[runStart - 1]))) {
        runStart -= 1;
    }
    const trailingStart = code.indexOf("}", runStart);

    if (trailingStart === -1 || !/[^\s,;]/.test(code.slice(0, trailingStart))) {
        return undefined;
    }
    let depth = 0;
    for (let index = 0; index < trailingStart; index += 1) {
        if (code[index] === "{") {
            depth += 1;
        } else if (code[index] === "}") {
            depth = Math.max(0, depth - 1);
        }
    }
    const closers = (code.slice(trailingStart).match(/}/g) ?? []).length;
    const unmatched = closers - depth;
    if (unmatched <= 0) {
        return undefined;
    }
    // keep the braces that close tables opened on this line
    let offset = trailingStart;
    let kept = depth;
    while (kept > 0) {
        if (code[offset] === "}") {
            kept -= 1;
        }
        offset += 1;
    }
    return offset;
};

/** Emits every closing brace of the tail on its own line. */
const splitClosers = (tail: DiffSegment[]): DiffSegment[][] => {
    const result: DiffSegment[][] = [];

    tail.forEach((segment) => {
        if (segment.operation === "delete") {
            const lastLine = result.at(-1);
            if (lastLine) {
                lastLine.push(segment);
            } else {
                result.push([segment]);
            }
            return;
        }
        [...segment.text].filter((char) => char === "}").forEach(() => result.push([{ ...segment, text: "}" }]));
    });
    return result;
};

/**
 * Reshapes lua/json lines so that a closing brace is on its own line together with its separator:
 * `a="x",}` + `,` becomes `a="x",` + `},`.
 */
const reshapeBraceLines = (lines: DiffSegment[][]): DiffSegment[][] => {
    const result: DiffSegment[][] = [];

    lines.forEach((rawSegments) => {
        let segments = stripLeadingWhitespace(rawSegments);
        const previous = result.at(-1);
        const [first] = segments;

        if (
            previous &&
            first &&
            first.operation !== "delete" &&
            first.text.startsWith(",") &&
            getNewText(previous).trimEnd().endsWith("}")
        ) {
            previous.push({ ...first, text: "," });
            segments = stripLeadingWhitespace([{ ...first, text: first.text.slice(1) }, ...segments.slice(1)]);
            if (segments.every(({ text }) => text === "")) {
                return;
            }
        }

        const offset = findTrailingClosers(getNewText(segments));
        if (offset === undefined) {
            result.push(segments);
            return;
        }
        const [head, tail] = splitSegments(segments, offset);
        result.push(head, ...splitClosers(tail));
    });
    return result;
};

/** Removes xml comments; an unterminated comment runs to the end of the line. */
const stripXmlComments = (line: string): string => {
    let result = "";
    let index = 0;

    while (index < line.length) {
        const start = line.indexOf("<!--", index);
        if (start === -1) {
            return result + line.slice(index);
        }
        result += line.slice(index, start);
        const end = line.indexOf("-->", start + 4);
        if (end === -1) {
            return result;
        }
        index = end + 3;
    }
    return result;
};

const measureXmlTags = (line: string) => {
    const code = stripXmlComments(line);
    let delta = 0;

    for (const [tag, closing] of code.matchAll(/<(\/?)[A-Za-z_][^<>]*>/g)) {
        if (closing) {
            delta -= 1;
        } else if (!tag.endsWith("/>")) {
            delta += 1;
        }
    }
    return { delta, leadingClosers: code.trimStart().startsWith("</") ? 1 : 0 };
};

/**
 * Converts diff HTML into lines with the pilcrow markers removed and, for structured
 * file types, leading whitespace normalised to an indentation level based on nesting depth.
 * Depth is tracked on the new version of the text (equal + inserted segments).
 */
export const formatDiff = (fileName: string, html: string): DiffLine[] => {
    const mode = getIndentMode(fileName);
    const lines = parseDiffHtml(html);

    if (mode === "none") {
        return lines.map((segments) => ({ indent: 0, segments }));
    }

    const measure = mode === "braces" ? measureBraces : measureXmlTags;
    const shapedLines = mode === "braces" ? reshapeBraceLines(lines) : lines;
    let depth = 0;

    return shapedLines.map((rawSegments) => {
        const segments = stripLeadingWhitespace(rawSegments);
        const { delta, leadingClosers } = measure(getNewText(segments));
        const indent = Math.max(0, depth - leadingClosers);

        depth = Math.max(0, depth + delta);
        return { indent, segments };
    });
};
